import React, {useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Popover, PopoverPlacement} from '@vio/atlas/Popover'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {getAnonymousId} from '@vio/core/src/_web/anonymousId'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {
  TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY,
  trackUserAuthClick
} from '@vio/core/src/auth/modules/actions'
import {
  getUser,
  selectIsAuthenticated,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'
import {Connections} from '@vio/core/src/auth/modules/selectors.shared'
import {makeSocialLogin} from '@vio/core/src/auth/modules/thunks'
import {
  AuthenticationFlows,
  AuthenticationMethod,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import {getMyBookingsUrl} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getLanguageCode} from '@vio/core/src/localization/services/locale'

import {useLogOut} from '../../../../authentication/hooks/useLogOut'
import {AuthenticatedMenuItems} from '../../../../user/account/AuthenticatedMenuItems'
import {UserAvatar} from '../../../../user/account/UserAvatar'
import {KIWI_FONT_STACK} from '../constants'
import {LoggedPersonIcon} from './assets/LoggedPersonIcon'
import {MenuIcon} from './assets/MenuIcon'
import {PersonIcon} from './assets/PersonIcon'

const SignInTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s300};
  background: none;
  border: none;
  cursor: pointer;
  min-height: 44px;
  margin: 0;
  padding: ${cssTheme.layout.spacing.s400};
  border-radius: 6px;
  color: ${cssTheme.colors.content.neutral.primary};
  font-family: ${KIWI_FONT_STACK};
  font-size: 15px;
  font-weight: 500;
  line-height: 15px;
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
  transition: background cubic-bezier(0.4, 0, 0.2, 1) 0.15s;

  &:hover {
    fill: ${cssTheme.colors.content.brand.primary};
    color: ${cssTheme.colors.content.brand.primary};
  }

  &:focus-visible {
    outline: 2px solid ${cssTheme.colors.border.brand.primary};
    outline-offset: 2px;
  }
`

const MenuIconTrigger = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  padding: ${cssTheme.layout.spacing.s400};
`

const Pill = styled.button`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s300};
  background: none;
  border: 1px solid ${cssTheme.colors.neutral.c100};
  border-radius: ${cssTheme.layout.radius.rounded};
  cursor: pointer;
  padding: ${cssTheme.layout.spacing.s300};
  color: ${cssTheme.colors.content.neutral.primary};
  transition: background cubic-bezier(0.4, 0, 0.2, 1) 0.15s;
  margin: 0;

  &:hover {
    background-color: ${cssTheme.colors.background.neutral.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${cssTheme.colors.border.brand.primary};
    outline-offset: 2px;
  }

  ${linariaMq.desktopXs} {
    margin-left: ${cssTheme.layout.spacing.s400};
  }
`

const PillAvatarSlot = styled.span`
  display: inline-flex;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
`

interface Props {
  /** Callback to open the preferences overlay (mobile signed-in click) */
  onShowPreferences?: () => void
}

export const KiwiUserWidget = ({onShowPreferences}: Props) => {
  const intl = useIntl()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectIsLoading)
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const logOut = useLogOut()
  const {brand, brandCode} = useBrand()
  const {
    bookings: {hasMyBookings}
  } = useBrandCapabilities()
  const locale = useSelector(getLanguageCode)
  const {isMobile} = useDeviceLayout()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const nonAuthenticatedAnchorRef = useRef<HTMLDivElement>(null)

  const togglePopover = () => {
    setPopoverOpen(open => !open)
  }

  const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
    // If the click is inside the anchor, don't close the popover because the anchor will do that.
    if (anchorRef.current?.contains(event.target as Node)) return
    setPopoverOpen(false)
  }

  if (isLoading) return null

  const myBookingsUrl = hasMyBookings
    ? getMyBookingsUrl({
        brand,
        appEnv: process.env.NEXT_PUBLIC_ENV!,
        anonymousId: getAnonymousId(),
        locale
      })
    : undefined

  const handleSignInClick = () => {
    const analyticsData = {authenticationMethod: brandCode}

    dispatch(
      trackUserAuthClick({
        authenticationMethod: AuthenticationMethod.OIDCFederation,
        sourceComponent: SourceComponentType.Header,
        authenticationFlow: AuthenticationFlows.SignUp,
        analyticsData
      })
    )

    setTimeout(
      () =>
        dispatch(
          makeSocialLogin({
            connection: Connections.OIDCFederation,
            unlockHotelId: '',
            sourceComponent: SourceComponentType.Header,
            flow: AuthenticationFlows.SignUp,
            analyticsData
          })
        ),
      TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY
    )
  }

  const handlePillClick = () => {
    if (isMobile && onShowPreferences) {
      onShowPreferences()
      return
    }
    togglePopover()
  }

  if (!isAuthenticated) {
    return (
      <div ref={nonAuthenticatedAnchorRef}>
        <Box display="flex" alignItems="center">
          <SignInTrigger
            type="button"
            onClick={handleSignInClick}
            data-id="KiwiSignInButton"
          >
            <PersonIcon size={20} />
            <FormattedMessageWrapper
              id="signInButton"
              defaultMessage="Sign in"
            />
          </SignInTrigger>
          <MenuIconTrigger
            type="button"
            onClick={handlePillClick}
            data-id="KiwiMenuIconTrigger"
            data-testid="KiwiMenuIconTrigger"
          >
            <MenuIcon size={20} />
          </MenuIconTrigger>
        </Box>
        <Popover
          isOpen={popoverOpen}
          anchorRef={nonAuthenticatedAnchorRef}
          onOutsideClick={handleOutsideClick}
          placement={PopoverPlacement.BottomRight}
        >
          <AuthenticatedMenuItems
            alertsUrl="/user/alerts"
            myBookingsUrl={myBookingsUrl}
            onLogOut={isAuthenticated ? logOut : undefined}
            isAuthenticated={Boolean(isAuthenticated)}
          />
        </Popover>
      </div>
    )
  }

  return (
    <>
      <Pill
        ref={anchorRef}
        type="button"
        onClick={handlePillClick}
        aria-label={intl.formatMessage({
          id: 'userWidget.myProfile',
          defaultMessage: 'My profile'
        })}
        aria-haspopup={!isMobile ? 'menu' : undefined}
        aria-expanded={!isMobile ? popoverOpen : undefined}
        data-id="KiwiUserWidgetPill"
        data-testid="KiwiUserWidgetPill"
        className="fs-exclude"
      >
        <PillAvatarSlot>
          <UserAvatar
            user={user || null}
            icon={<LoggedPersonIcon size={24} />}
          />
        </PillAvatarSlot>
        <MenuIcon size={20} />
      </Pill>
      {!isMobile && (
        <Popover
          isOpen={popoverOpen}
          anchorRef={anchorRef}
          onOutsideClick={handleOutsideClick}
          placement={PopoverPlacement.BottomRight}
        >
          <AuthenticatedMenuItems
            alertsUrl="/user/alerts"
            myBookingsUrl={myBookingsUrl}
            onLogOut={isAuthenticated ? logOut : undefined}
            isAuthenticated={Boolean(isAuthenticated)}
          />
        </Popover>
      )}
    </>
  )
}
